import React from 'react';
// import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SectionTitle from '../components/SectionTitle';

const TargetGroupsPage: React.FC = () => {
  // const { t } = useTranslation();

  // Target groups with detailed information
  const targetGroups = [
    {
      id: 'gastronomy',
      name: 'Gastronomy & Restaurants', // t('targetGroups.gastronomy')
      icon: '🍽️',
      title: 'For Gastronomy & Restaurants', // 'Gastronomie & Restaurants'
      description: 'Most restaurants rely on platforms to be found online. However, their fees drastically reduce already slim margins.',
      problems: [
        'High commissions on delivery services (up to 30%)',
        'Dependence on review platforms',
        'Expensive table reservation systems',
        'Complex or outdated digital solutions'
      ],
      solutions: [
        'Own website with integrated ordering system',
        'Independent reservation system without subscription costs',
        'Direct delivery system without commissions',
        'Easy management of menus and offers'
      ],
      imagePath: '/images/target/target-gastronomy_left.jpg'
    },
    {
      id: 'retail',
      name: 'Retail', // t('targetGroups.retail')
      icon: '🛍️',
      title: 'For Retailers', // 'Einzelhandel & Geschäfte'
      description: 'Online marketplaces promise reach but often dictate prices and conditions. Own online shops offer more control.',
      problems: [
        'High sales commissions on marketplaces',
        'Dependence on platform algorithms',
        'Lack of direct customer contact',
        'Difficulty in building an own brand'
      ],
      solutions: [
        'Own online shop with direct sales',
        'Full control over prices and conditions',
        'Direct customer communication and loyalty',
        'Independent brand building'
      ],
      imagePath: '/images/target/target-retail.jpg'
    },
    {
      id: 'coaches',
      name: 'Coaches & Consultants', // t('targetGroups.coaches')
      icon: '📊',
      title: 'For Coaches & Consultants', // 'Coaches & Berater:innen'
      description: 'Booking platforms for coaches often charge high fees or restrict direct communication with clients.',
      problems: [
        'High fees on coaching platforms',
        'Dependence on platform reviews',
        'Lack of personalization options',
        'Difficulty in direct client communication'
      ],
      solutions: [
        'Own website with integrated booking tool',
        'Direct client communication and management',
        'Flexible design and content options',
        'No commission on bookings'
      ],
      imagePath: '/images/target/target-coach.jpg'
    },
    {
      id: 'creatives',
      name: 'Creatives & Artists', // t('targetGroups.creatives')
      icon: '🎨',
      title: 'For Creatives & Artists', // 'Kreative & Künstler:innen'
      description: 'Portfolio platforms can be restrictive. Own websites offer more freedom to showcase work authentically.',
      problems: [
        'Limited presentation options on platforms',
        'High fees for premium features',
        'Dependence on platform visibility',
        'Difficulty in selling directly'
      ],
      solutions: [
        'Individual portfolio website',
        'Full control over design and presentation',
        'Direct contact and sales opportunities',
        'Independent online presence'
      ],
      imagePath: '/images/target/target-creative.jpg'
    }
  ];

  return (
    <main className="pt-24 pb-16">
      {/* Header Section */}
      <section className="bg-primary/5 py-16">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <SectionTitle 
            title="For Whom?" // {t('targetGroups.title')}
            subtitle="Solutions for various industries affected by platform dependency." // {t('targetGroups.subtitle')}
            centered
          />
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gray-600 text-lg leading-relaxed">
              Many small businesses and self-employed individuals feel the pressure of large digital platforms. Whether it's high commissions, restrictive terms, or the fear of suddenly losing visibility – the dependency is a burden for many. We develop solutions that give you back control over your digital presence.
            </p>
          </div>
        </div>
      </section>

      {/* Target Groups Detailed */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="space-y-20">
            {targetGroups.map((group, index) => (
              <div 
                key={group.id} 
                id={group.id}
                className={`scroll-mt-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="image-with-text-overlay rounded-xl overflow-hidden h-full min-h-[300px]">
                    <img 
                      src={group.imagePath} 
                      alt={group.title} 
                      className="w-full h-full object-cover"
                    />
                    <div className="overlay"></div>
                    <div className="text-content p-8">
                      <div className="flex items-center mb-6">
                        <span className="text-4xl mr-4">{group.icon}</span>
                        <h3 className="font-lexend font-bold text-2xl text-white">
                          {group.title}
                        </h3>
                      </div>
                      <p className="text-white mb-8 opacity-90">
                        {group.description}
                      </p>
                      <Link 
                        to="/loesungen" 
                        className="inline-flex items-center font-lexend font-medium text-white bg-primary hover:bg-primary-600 px-4 py-2 rounded transition-all"
                        style={{ opacity: 0.8 }}
                      >
                        Unsere Lösungen für {group.name} <span className="ml-1">→</span>
                      </Link>
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="bg-white dark:bg-deepblue-900 p-8 rounded-xl shadow-sm mb-8">
                    <h4 className="font-lexend font-semibold text-xl text-gray-900 dark:text-white mb-4 flex items-center">
                      <svg className="w-6 h-6 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Typische Probleme
                    </h4>
                    <ul className="space-y-3 text-gray-800 dark:text-gray-200">
                      {group.problems.map((problem, i) => (
                        <li key={i} className="flex items-start">
                          <svg className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          {problem}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-white dark:bg-deepblue-900 p-8 rounded-xl shadow-sm">
                    <h4 className="font-lexend font-semibold text-xl text-gray-900 dark:text-white mb-4 flex items-center">
                      <svg className="w-6 h-6 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Unsere Lösungsansätze
                    </h4>
                    <ul className="space-y-3 text-gray-800 dark:text-gray-200">
                      {group.solutions.map((solution, i) => (
                        <li key={i} className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {solution}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-lexend font-bold text-2xl text-dark mb-6">
              Is Your Industry Not Listed?
            </h2>
            <p className="text-gray-600 mb-8">
              The problem of platform dependency affects many sectors. If you don't see your industry here, or have specific needs, feel free to contact us. We are always looking to expand our solutions and help more people achieve digital independence.
            </p>
            <Link 
              to="/kontakt" 
              className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-md font-lexend font-medium text-base shadow-sm transition-all"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TargetGroupsPage; 